import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, Grid, Text, Select, Input, InputGroup, InputLeftElement, Icon, IconButton, Spinner,
} from '@chakra-ui/react';
import { FiSearch, FiEye } from 'react-icons/fi';
import dayjs from 'dayjs';
import {
  getAiSummary, getAiSittings, getAiFilters, SITTING_STATUS, reasonLabel, formatScore,
} from 'api/administrador/monitoring/aiGrading';
import { KpiCard, PeriodPicker, ChipGroup, StatusPill } from './common';
import AiPagination from './AiPagination';

const PAGE_SIZE = 20;
const COLS = '2.4fr 1.1fr 0.6fr 1.6fr 60px';

function sentLabel(date) {
  const d = dayjs(date);
  if (d.isSame(dayjs(), 'day')) return `Hoy ${d.format('HH:mm')}`;
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return `Ayer ${d.format('HH:mm')}`;
  return d.format('DD MMM · HH:mm');
}

function publishLabel(date) {
  const d = dayjs(date);
  if (d.isSame(dayjs(), 'day')) return `Sale hoy a las ${d.format('HH:mm')}`;
  if (d.isSame(dayjs().add(1, 'day'), 'day')) return `Sale mañana a las ${d.format('HH:mm')}`;
  return `Sale el ${d.format('DD MMM')} a las ${d.format('HH:mm')}`;
}

function statusDetail(row, queue) {
  if (row.status === 'to_publish') {
    if (queue?.releasePaused) return 'Publicación en pausa';
    return row.publishAt ? publishLabel(row.publishAt) : null;
  }
  if (row.status === 'held') return `Retenida por ${row.hold?.by?.name || 'un administrador'}`;
  if (row.status === 'published') {
    if (row.reviewRequested) return 'En revisión del docente';
    return row.releasedAt ? sentLabel(row.releasedAt) : null;
  }
  if (row.status === 'teacher') return reasonLabel(row.reason);
  if (row.status === 'teacher_graded') return row.score !== null ? `Nota del docente ${formatScore(row.score)}` : null;
  if (row.status === 'queued') return row.attempts ? `Reintento ${row.attempts} de 3` : 'Esperando la siguiente ejecución';
  return null;
}

function Thumb({ kind }) {
  return (
    <Flex
      w="40px" h="48px" flexShrink={0} borderRadius="6px" align="center" justify="center"
      color="white" fontSize="10px" fontWeight="700"
      bg={kind === 'PDF' ? 'linear-gradient(135deg,#1E293B,#334155)' : 'linear-gradient(135deg,#6D48E5,#A855F7)'}
    >
      {kind}
    </Flex>
  );
}

export default function SittingsTab({ tick, queue, onOpenSitting }) {
  // tick también llega tras cada acción del panel, así la tabla refleja el cambio.
  const [filters, setFilters] = useState({ period: 'today', program: '', subjectId: '', status: '', search: '', page: 1 });
  const [searchText, setSearchText] = useState('');
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ programs: [] });

  useEffect(() => {
    getAiFilters().then(setOptions).catch(() => setOptions({ programs: [] }));
  }, []);

  // Búsqueda con pequeña espera para no consultar en cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchText ? f : { ...f, search: searchText, page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchText]);

  const base = useMemo(
    () => ({ period: filters.period, program: filters.program, subjectId: filters.subjectId, search: filters.search }),
    [filters.period, filters.program, filters.subjectId, filters.search],
  );

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      const [s, list] = await Promise.all([
        getAiSummary(base),
        getAiSittings({ ...base, status: filters.status, page: filters.page, pageSize: PAGE_SIZE }),
      ]);
      setSummary(s);
      setRows(list?.data || []);
      setPagination(list?.pagination || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[calificación] error cargando entregas:', e);
    } finally {
      setLoading(false);
    }
  }, [base, filters.status, filters.page]);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => { if (tick) load(true); }, [tick]);

  const program = options.programs.find((p) => p.value === filters.program);
  const subjects = program ? program.subjects : options.programs.flatMap((p) => p.subjects.map((s) => ({ ...s, name: `${s.name} · ${p.name}` })));
  const k = summary?.kpis;
  const st = summary?.byStatus || {};
  const total = Object.values(st).reduce((a, b) => a + b, 0);

  const chips = [
    { value: '', label: `Todas · ${total}` },
    { value: 'to_publish', label: `Por publicar · ${st.to_publish || 0}` },
    { value: 'held', label: `Retenidas · ${st.held || 0}` },
    { value: 'published', label: `Publicadas · ${st.published || 0}` },
    { value: 'teacher', label: `Al docente · ${st.teacher || 0}` },
    { value: 'queued', label: `En cola · ${st.queued || 0}` },
    { value: 'teacher_graded', label: `Calificadas por el docente · ${st.teacher_graded || 0}` },
  ];

  return (
    <Box>
      <Flex align="center" justify="space-between" gap={4} mb={4} wrap="wrap">
        <PeriodPicker value={filters.period} onChange={(period) => setFilters({ ...filters, period, page: 1 })} />
        <Flex gap={3} wrap="wrap">
          <Box>
            <Text as="label" htmlFor="ai-program" fontSize="11px" fontWeight="700" letterSpacing=".05em" color="gray.500" textTransform="uppercase">
              Programa
            </Text>
            <Select
              id="ai-program" size="md" w="240px" bg="white" mt={1} borderRadius="8px"
              value={filters.program}
              onChange={(e) => setFilters({ ...filters, program: e.target.value, subjectId: '', page: 1 })}
            >
              <option value="">Todos los programas</option>
              {options.programs.map((p) => <option key={p.value} value={p.value}>{p.name}</option>)}
            </Select>
          </Box>
          <Box>
            <Text as="label" htmlFor="ai-subject" fontSize="11px" fontWeight="700" letterSpacing=".05em" color="gray.500" textTransform="uppercase">
              Materia
            </Text>
            <Select
              id="ai-subject" size="md" w="240px" bg="white" mt={1} borderRadius="8px"
              value={filters.subjectId}
              onChange={(e) => setFilters({ ...filters, subjectId: e.target.value, page: 1 })}
            >
              <option value="">Todas las materias</option>
              {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Box>
        </Flex>
      </Flex>

      <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(5, minmax(0, 1fr))' }} gap={3} mb={5}>
        <KpiCard loading={!summary} label="Recibidas" value={k?.received ?? 0} foot="envío nuevo" color="#574feb" />
        <KpiCard loading={!summary} label="Por publicar" value={k?.toPublish ?? 0} foot="calificadas, esperan su hora" color="#3B82F6" />
        <KpiCard loading={!summary} label="Publicadas" value={k?.published ?? 0} foot="notificadas al estudiante" color="#10B981" />
        <KpiCard
          loading={!summary} label="Nota promedio" value={formatScore(k?.avgScore)} color="#8B5CF6"
          foot={k?.teacherAvgScore !== null && k?.teacherAvgScore !== undefined ? `docente: ${formatScore(k.teacherAvgScore)}` : 'sin notas del docente en el periodo'}
        />
        <KpiCard
          loading={!summary} label="Costo" color="#94A3B8"
          value={k ? `$${k.cost.toFixed(k.cost < 1 ? 3 : 2).replace('.', ',')}` : '$0'}
          foot={k?.costPerSitting ? `≈ $${k.costPerSitting.toFixed(4).replace('.', ',')} por entrega` : 'estimado por tokens'}
        />
      </Grid>

      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px">
        <Flex px={5} py={4} gap={3} justify="space-between" align="center" wrap="wrap">
          <ChipGroup ariaLabel="Estado" options={chips} value={filters.status} onChange={(status) => setFilters({ ...filters, status, page: 1 })} />
          <InputGroup w={{ base: '100%', md: '320px' }}>
            <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color="gray.400" /></InputLeftElement>
            <Input
              aria-label="Buscar entregas"
              placeholder="Buscar estudiante, actividad o materia…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              borderRadius="8px"
            />
          </InputGroup>
        </Flex>

        <Grid templateColumns={COLS} px={5} py={3} bg="gray.50" fontSize="11px" fontWeight="600" letterSpacing=".06em" color="gray.600" textTransform="uppercase">
          <Text>Entrega</Text><Text>Enviada</Text><Text>Nota</Text><Text>Estado</Text><Text />
        </Grid>

        {loading && rows.length === 0 && (
          <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>
        )}
        {!loading && rows.length === 0 && (
          <Text textAlign="center" py={10} color="gray.500" fontSize="sm">
            No hay entregas con esos filtros en el periodo.
          </Text>
        )}
        {rows.map((r) => {
          const s = SITTING_STATUS[r.status] || SITTING_STATUS.queued;
          const meta = [r.student?.name, r.subject?.name, r.program?.name].filter(Boolean).join(' · ');
          const detail = statusDetail(r, queue);
          return (
            <Grid key={r.id} templateColumns={COLS} px={5} py={3.5} alignItems="center" borderTop="1px solid" borderColor="gray.100">
              <Flex align="center" gap={3} minW={0}>
                <Thumb kind={r.kind} />
                <Box minW={0}>
                  <Text fontSize="sm" fontWeight="600" isTruncated title={r.activity?.title}>{r.activity?.title || `Actividad ${r.activity?.id}`}</Text>
                  <Text fontSize="xs" color="gray.500" isTruncated>{meta}</Text>
                </Box>
              </Flex>
              <Text fontSize="sm" color="gray.700">{sentLabel(r.createdAt)}</Text>
              <Text fontSize="15px" fontWeight="700" color="gray.900">
                {r.status === 'queued' ? '…' : r.status === 'teacher' ? '—' : formatScore(r.status === 'teacher_graded' ? r.score : r.aiScore)}
              </Text>
              <Box>
                <StatusPill label={s.label} tone={s.tone} />
                {detail && <Text fontSize="xs" color="gray.500" mt={1}>{detail}</Text>}
              </Box>
              <Flex justify="flex-end">
                <IconButton
                  aria-label="Ver detalle de la entrega" icon={<FiEye />} size="sm" variant="outline"
                  borderRadius="8px" onClick={() => onOpenSitting(r.id)}
                />
              </Flex>
            </Grid>
          );
        })}

        <AiPagination
          pagination={pagination}
          noun="entregas"
          extra="se actualiza cada 30 s"
          onPageChange={(page) => setFilters({ ...filters, page })}
        />
      </Box>
    </Box>
  );
}
